import React, { useState } from 'react';
import { GraduationCapIcon, LockIcon, RefreshCwIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { CaseQueue } from '../../components/verification/CaseQueue';
import { useVerification } from '../../contexts/VerificationContext';
import { STATUS_LABEL, formatDate, nextLifecycle } from '../../utils/verification';
const TABS = ['Requests', 'Structure', 'Lifecycle'] as const;

/** The institution this console is signed in as. Scope is not selectable here. */
const MY_INSTITUTION = 'inst-futa';
const OFFICER = '@futa.registry';

/**
 * The institution's own verification workspace. It confirms affiliation — the
 * strongest claim in the system — and can see nothing outside its own tenant.
 */
export function AcademicVerification() {
  const {
    institutions,
    records,
    reportStatus
  } = useVerification();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Requests');
  const institution = institutions.find(entry => entry.id === MY_INSTITUTION);
  const ourRecords = records.filter(record => record.institutionId === MY_INSTITUTION);
  return <div className="space-y-6" data-id="element-8399" data-mp="3spx7mmolklr3">
      <PageHeader eyebrow="Academic identity" title="Student verification" description="Confirm that a person's claimed affiliation matches your own register. Your confirmation is the strongest verification Campuzon can hold." actions={<Badge plain data-id="element-8401" data-mp="3eokvynggz6b0">{institution?.name}</Badge>} data-id="element-8400" data-mp="3sovn7rloymwp" />

      <Panel data-id="element-8402" data-mp="3er7cvdp92y5w">
        <p className="flex max-w-[80ch] items-start gap-2 text-body-sm text-ink" data-id="element-8403" data-mp="39q9noh5tp3uw">
          <LockIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-8404" data-mp="39smbhay9i8fv" />
          This console is scoped to {institution?.name}. Requests from other institutions are not
          reachable from here, and confirming a student gives you no access to their messages, notes,
          housing, financial records or activity elsewhere on Campuzon.
        </p>
      </Panel>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Verification sections" data-id="element-8405" data-mp="1vvjcu20xg9zg" />

      {tab === 'Requests' && <CaseQueue institutionId={MY_INSTITUTION} reviewer={OFFICER} mode="institution" data-id="element-8406" data-mp="1i10h29fho4q4" />}

      {tab === 'Structure' && institution && <div className="space-y-4" data-id="element-8407" data-mp="2ask7oeef0l25">
          <Panel title="Your academic structure" description="These labels and values shape how Campuzon asks students about their record, and what it can verify." data-id="element-8408" data-mp="2apefgqahp327">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-8409" data-mp="20vffnxxw1vdf">
              <div data-id="element-8410" data-mp="25r4hy91alqdf">
                <dt className="text-body-sm text-ink-muted" data-id="element-8411" data-mp="25ql4b4iodo57">Division label</dt>
                <dd className="text-body text-ink" data-id="element-8412" data-mp="pdeaqtevmlzk">{institution.structure.divisionLabel}</dd>
              </div>
              <div data-id="element-8413" data-mp="uek7r3fkkvew">
                <dt className="text-body-sm text-ink-muted" data-id="element-8414" data-mp="uawf06hy8d18">Year of study label</dt>
                <dd className="text-body text-ink" data-id="element-8415" data-mp="mrwyq9psb1m3">
                  {institution.structure.levelLabel ?? 'Not used'}
                </dd>
              </div>
              <div data-id="element-8416" data-mp="vuo7g1r9zja1">
                <dt className="text-body-sm text-ink-muted" data-id="element-8417" data-mp="vtwd0u9ezrlc">Identifier label</dt>
                <dd className="text-body text-ink" data-id="element-8418" data-mp="qwedtvl78qqj">{institution.structure.identifierLabel}</dd>
              </div>
              <div data-id="element-8419" data-mp="36jzf9l8eu46">
                <dt className="text-body-sm text-ink-muted" data-id="element-8420" data-mp="33o47t0rqjjf">Highest level supported</dt>
                <dd className="text-body text-ink" data-id="element-8421" data-mp="3vb4cxjhu3y49">
                  {institution.maxLevelSupported.replace(/_/g, ' ')}
                </dd>
              </div>
            </dl>
            <div className="mt-4 border-t border-line pt-4" data-id="element-8422" data-mp="83skpj1ofe9n">
              <h2 className="text-body font-semibold text-ink" data-id="element-8423" data-mp="3gkdnuj7mnd4u">Statuses you recognise</h2>
              <ul className="mt-2 flex flex-wrap gap-2" data-id="element-8424" data-mp="3blk96ngltg33">
                {institution.structure.statuses.map(status => <li key={status} data-id="element-8425" data-mp="3p8sytk1o804i">
                    <Badge plain data-id="element-8426" data-mp="3p9uek1tmtask">{STATUS_LABEL[status]}</Badge>
                  </li>)}
              </ul>
              <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-8427" data-mp="3u59m6c11qagj">
                Statuses that carry disciplinary meaning are never displayed outside the student and
                your own staff, regardless of the student's privacy settings.
              </p>
            </div>
          </Panel>

          <Panel title="Accepted documents" data-id="element-8428" data-mp="bvqy6z2msajk">
            <ul className="space-y-1.5" data-id="element-8429" data-mp="grg5jokw6vqf">
              {institution.acceptedDocuments.map(entry => <li key={entry} className="text-body text-ink" data-id="element-8430" data-mp="lsvpfdbc2w78">
                  {entry}
                </li>)}
            </ul>
            <p className="mt-3 max-w-[76ch] text-body-sm text-ink-muted" data-id="element-8431" data-mp="qob05we1obzf">
              Campuzon reviews these documents. A document review is labelled as exactly that on the
              student's profile — it never claims your institution confirmed anything.
            </p>
          </Panel>
        </div>}

      {tab === 'Lifecycle' && <div className="space-y-4" data-id="element-8432" data-mp="14v4in5az5avs">
          <Panel title="Reporting a change" data-id="element-8433" data-mp="14u2y8a82jc1e">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8434" data-mp="10tss8xka0lmj">
              When you report a status change, Campuzon moves the academic identity and keeps the
              history. Graduation does not delete the verified relationship — it becomes an alumni
              record, and the profile stops saying “current student” without losing what was true.
            </p>
          </Panel>
          <Panel bodyClassName="p-0" data-id="element-8435" data-mp="1jkk7uguux89d">
            <ul className="divide-y divide-line" data-id="element-8436" data-mp="1jjimnjk4n5dx">
              {ourRecords.map(record => {
            const next = nextLifecycle(record.status);
            return <li key={record.id} className="px-5 py-4 sm:px-6" data-id="element-8437" data-mp="86y6zyfmxi80">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2.5" data-id="element-8438" data-mp="373lybijjmuv">
                      <div className="min-w-0" data-id="element-8439" data-mp="4o8z1kyqj9aw">
                        <p className="flex flex-wrap items-center gap-2 text-body font-semibold text-ink" data-id="element-8440" data-mp="4oirdf795sjr">
                          <GraduationCapIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-8441" data-mp="9manymiue2ua" />
                          {record.programme.value}
                        </p>
                        <p className="text-body-sm text-ink-muted" data-id="element-8442" data-mp="kuf4j7oe8s6">
                          {record.level?.value ?? '—'} · {record.academicSession}
                        </p>
                        <p className="mt-1 text-body-sm text-ink" data-id="element-8443" data-mp="3munl6775qpkd">
                          Current status:{' '}
                          <span className="font-medium" data-id="element-8444" data-mp="393ieil5dgx0j">{STATUS_LABEL[record.status]}</span>
                          <span className="text-ink-muted" data-id="element-8445" data-mp="3d4u6gxbpxubw">
                            {' '}
                            · last updated {formatDate(record.updatedAt)}
                          </span>
                        </p>
                      </div>
                      {next && <Button variant="secondary" size="sm" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-8447" data-mp="2fjsouk8kuwxs" />} onClick={() => reportStatus(record.id, next, OFFICER)} data-id="element-8446" data-mp="2kidr0p7e3qov">
                          Report as {STATUS_LABEL[next].toLowerCase()}
                        </Button>}
                    </div>
                  </li>;
          })}
            </ul>
          </Panel>
          <Panel title="Revocation" data-id="element-8448" data-mp="2ygtxcf1cbf6h">
            <p className="max-w-[80ch] text-body-sm text-ink" data-id="element-8449" data-mp="3d5qer2pae4jr">
              You can withdraw a confirmation you previously gave. Doing so removes the badge and is
              recorded with your reason — it never deletes the historical record that the confirmation
              once existed, because that history is what makes the system auditable.
            </p>
          </Panel>
        </div>}
    </div>;
}